// @flow
import type { FSA } from 'types/redux';
import type { StoreState } from 'reducers';
import type { CountriesState } from 'reducers/countries';

import _ from 'lodash';
import * as RequestResultCases from 'middlewares/api-middleware';
import ViewModelBuilder from 'utils/view-model-builder';
import { CREATE_ENTITY, GET_ENTITIES, GET_ENTITY, SET_ACTIVE_ENTITY, DUPLICATE_ENTITY } from 'actions/common';
import modelsConfig from 'data/models-config';


export type EntityState = {
  entities: Array<Object>,
  active?: Object,
  newlyCreatedID?: number,
  continuationTokens?: Object,
}

export const defaultEntityState = {
  entities: [],
};

export function isCommonAction(action: FSA, actionName: string, stateKey: string, reqResult: string) {
  return _.get(action, 'type', '') === `${actionName}_${_.toUpper(_.snakeCase(stateKey))}${reqResult}`;
}

const entityReducer = (
  entityState: EntityState = defaultEntityState,
  action: FSA,
  modelConfig: Object,
  countriesState: CountriesState
): EntityState => {
  const { idField, stateKey, isContinuationToken, viewBuilderParams = {}, payloadKey: { list, fetch, create, duplicate } = {} } = modelConfig;
  const { payloadFetchTransform, payloadDuplicateTransform } = viewBuilderParams;
  _.set(viewBuilderParams, 'countriesState', countriesState);
  switch (true) {
    case isCommonAction(action, GET_ENTITIES, stateKey, RequestResultCases.SUCCESS): {
      const continuationTokens = _.get(entityState, 'continuationTokens', {});
      if (isContinuationToken) {
        const page = _.get(action, 'meta.request.params.page');
        const countryID = _.get(action, 'meta.request.params.countryID');
        const token = _.get(action, 'payload.token');
        if (page) {
          const key = parseInt(page, 10) + 1;
          _.set(continuationTokens, `${countryID}.${key}`, token);
        }
      }
      return {
        ...entityState,
        entities: _.map(_.get(action.payload, list, action.payload), entity =>
          new ViewModelBuilder(payloadFetchTransform ? payloadFetchTransform(entity, countriesState) : entity)
            .processByConfig(viewBuilderParams).build()
        ),
        continuationTokens,
      };
    }
    case isCommonAction(action, GET_ENTITY, stateKey, RequestResultCases.SUCCESS): {
      const payload = _.get(action.payload, fetch, action.payload);
      const getResponsePayload = _.isFunction(payloadFetchTransform) ? payloadFetchTransform(payload, countriesState) : payload;
      return {
        ...entityState,
        active: new ViewModelBuilder(getResponsePayload).processByConfig(viewBuilderParams).build(),
      };
    }
    case isCommonAction(action, DUPLICATE_ENTITY, stateKey, RequestResultCases.SUCCESS): {
      const payload = _.get(action.payload, duplicate, action.payload);
      const getResponsePayload = _.isFunction(payloadDuplicateTransform) ? payloadDuplicateTransform(payload, countriesState) : payload;
      return {
        ...entityState,
        active: new ViewModelBuilder(getResponsePayload).processByConfig(viewBuilderParams).build(),
      };
    }
    case isCommonAction(action, CREATE_ENTITY, stateKey, RequestResultCases.SUCCESS): {
      const createResponsePayload = payloadFetchTransform ? payloadFetchTransform(action.payload) : action.payload;
      return {
        ...entityState,
        newlyCreatedID: _.get(createResponsePayload, create, createResponsePayload)[idField],
      };
    }
    case isCommonAction(action, SET_ACTIVE_ENTITY, stateKey, ''):
      return {
        ...entityState,
        active: new ViewModelBuilder(action.payload).processByConfig(viewBuilderParams).build(),
      };
    default:
      return entityState;
  }
};

const common = (
  state: StoreState,
  action: FSA
): StoreState => {
  return _.reduce(modelsConfig, (resState, modelConfig) => {
    const entityState = _.get(state, modelConfig.stateKey);
    return {
      ...resState,
      [modelConfig.stateKey]: entityReducer(entityState, action, modelConfig, state.countries || {}),
    };
  },
  state);
};

export default common;
